import { action, computed, flow, flowResult, makeObservable, observable, runInAction } from 'mobx';
import { ApiResponse, GlobalStore, GridStore } from '@roc/feature-app-core';
import { DebtTrackingService, GridPageParams } from '../services/debtTrackingService';
import {
  BulkImportAction,
  BulkImportPreview,
  BulkImportResult,
  FinancingAmountChange,
  FinancingAmountValues,
  LOANS_PAGE_SIZE,
  LoanDebtDetail,
  SetFinancingAmountResponse,
  Warehouse,
  WarehouseSummary,
} from '../utils/debtTrackingConstants';
import { getApiErrorMessage, pluralize } from '../utils/debtTrackingUtils';

const SEARCH_DEBOUNCE_MS = 350;

export class DebtTrackingStore {
  globalStore: GlobalStore;
  service: DebtTrackingService;

  warehouses: Warehouse[] = [];
  selectedWarehouseId: number = null;
  warehouseSummary: WarehouseSummary = null;
  searchText = '';
  loansGridStore: GridStore;

  selectedLoanId: string = null;
  loanDetail: LoanDebtDetail = null;
  // Every value the open loan's financing amount had, newest first (History tab).
  loanHistory: FinancingAmountChange[] = [];

  bulkImportPreview: BulkImportPreview = null;
  bulkImportResult: BulkImportResult = null;

  private searchTimer: ReturnType<typeof setTimeout> = null;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.service = new DebtTrackingService();
    this.loansGridStore = new GridStore(() => this.fetchLoansPage(), null, LOANS_PAGE_SIZE);

    makeObservable(this, {
      warehouses: observable,
      selectedWarehouseId: observable,
      warehouseSummary: observable,
      searchText: observable,
      selectedLoanId: observable,
      loanDetail: observable,
      loanHistory: observable,
      bulkImportPreview: observable,
      bulkImportResult: observable,
      selectedWarehouse: computed,
      includedBulkImportRows: computed,
      setSearchText: action,
      closeLoanDetail: action,
      toggleBulkImportRow: action,
      resetBulkImport: action,
      fetchWarehouses: flow,
      selectWarehouse: flow,
      fetchWarehouseSummary: flow,
      openLoanDetail: flow,
      fetchLoanDetail: flow,
      fetchLoanHistory: flow,
      setFinancingAmount: flow,
      updateLoanFinancingAmount: flow,
      previewBulkImport: flow,
      applyBulkImport: flow,
    });
  }

  get selectedWarehouse() {
    return this.warehouses.find(warehouse => warehouse.id === this.selectedWarehouseId);
  }

  // ----- warehouse + loans grid (server-paginated) ----------------------------------------------------------------

  *fetchWarehouses() {
    try {
      const response: ApiResponse = yield this.service.getWarehouses();
      this.warehouses = response.data.data;
      if (!this.selectedWarehouseId && this.warehouses.length) {
        yield this.selectWarehouse(this.warehouses[0].id);
      }
    } catch (error) {
      this.showError(error, 'Error while fetching warehouses.');
    }
  }

  *selectWarehouse(warehouseId: number) {
    this.selectedWarehouseId = warehouseId;
    this.closeLoanDetail();
    yield Promise.all([this.fetchWarehouseSummary(), this.loansGridStore.resetAndFetchGridData()]);
  }

  *fetchWarehouseSummary() {
    if (!this.selectedWarehouseId) return;
    try {
      const response: ApiResponse = yield this.service.getWarehouseSummary(this.selectedWarehouseId);
      this.warehouseSummary = response.data.data;
    } catch (error) {
      this.warehouseSummary = null;
      this.showError(error, 'Error while fetching the warehouse totals.');
    }
  }

  // Server-side search on loan ID or last comment; keeps the current sort, goes back to page 1.
  setSearchText(searchText: string) {
    this.searchText = searchText;
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loansGridStore.setPageNumber(1), SEARCH_DEBOUNCE_MS);
  }

  private fetchLoansPage = async () => {
    const gridStore = this.loansGridStore;
    try {
      return await this.service.getWarehouseLoans(this.selectedWarehouseId, this.searchText.trim(),
        this.toPageParams(gridStore, LOANS_PAGE_SIZE));
    } catch (error) {
      this.showError(error, 'Error while fetching loans for this warehouse.');
      throw error;
    }
  };

  // ----- loan detail + financing amount history ------------------------------------------------------------------

  *openLoanDetail(loanId: string) {
    this.selectedLoanId = loanId;
    this.loanDetail = null;
    this.loanHistory = [];
    yield Promise.all([this.fetchLoanDetail(), this.fetchLoanHistory()]);
  }

  closeLoanDetail() {
    this.selectedLoanId = null;
    this.loanDetail = null;
  }

  *fetchLoanDetail() {
    try {
      const response: ApiResponse = yield this.service.getLoanDebtDetail(this.selectedLoanId);
      this.loanDetail = response.data.data;
    } catch (error) {
      this.showError(error, 'Error while fetching the loan.');
    }
  }

  *fetchLoanHistory() {
    try {
      const response: ApiResponse = yield this.service.getLoanHistory(this.selectedLoanId);
      this.loanHistory = response.data.data;
    } catch (error) {
      this.showError(error, 'Error while fetching the loan history.');
    }
  }

  // ----- writes -----------------------------------------------------------------------------------------------------

  // Sets the same financing amount on one or many loans of the selected warehouse (Add Entry). The amount REPLACES
  // each loan's current value; new loan IDs are created. Returns an error message for the dialog, or null on success.
  *setFinancingAmount(loanIds: string[], values: FinancingAmountValues) {
    try {
      const response: ApiResponse = yield this.service.setFinancingAmount({ ...values, loanIds, warehouseId: this.selectedWarehouseId });
      const { createdLoanIds, updatedLoanIds }: SetFinancingAmountResponse = response.data.data;
      this.globalStore.notificationStore.showSuccessNotification({
        message: `Created ${pluralize(createdLoanIds.length, 'loan')} and updated ${pluralize(updatedLoanIds.length, 'loan')}.`,
      });
      yield this.refreshAfterChange();
      return null;
    } catch (error) {
      return getApiErrorMessage(error, 'Error while saving the financing amount. Please try again.');
    }
  }

  // Entry tab of the open loan: replaces its financing amount, and moves it to warehouseId when that is another
  // warehouse (the API only allows it when the saved or the new amount is 0). Returns an error message, or null on success.
  *updateLoanFinancingAmount(values: FinancingAmountValues, warehouseId: number) {
    try {
      yield this.service.setFinancingAmount({
        ...values,
        loanIds: [this.selectedLoanId],
        warehouseId: warehouseId ?? this.loanDetail?.warehouse?.id ?? this.selectedWarehouseId,
      });
      this.globalStore.notificationStore.showSuccessNotification({ message: 'Loan updated.' });
      yield this.refreshAfterChange();
      return null;
    } catch (error) {
      return getApiErrorMessage(error, 'Error while saving the financing amount. Please try again.');
    }
  }

  // ----- bulk import ------------------------------------------------------------------------------------------------

  *previewBulkImport(file: File) {
    try {
      const response: ApiResponse = yield this.service.previewBulkImport(file);
      const preview: BulkImportPreview = response.data.data;
      this.bulkImportPreview = {
        ...preview,
        rows: preview.rows.map(row => ({ ...row, include: row.action !== BulkImportAction.ERROR })),
      };
      return null;
    } catch (error) {
      return getApiErrorMessage(error, 'Error while reading the file.');
    }
  }

  toggleBulkImportRow(rowNumber: number) {
    this.bulkImportPreview.rows = this.bulkImportPreview.rows.map(row =>
      row.rowNumber === rowNumber && row.action !== BulkImportAction.ERROR ? { ...row, include: !row.include } : row);
  }

  get includedBulkImportRows() {
    return this.bulkImportPreview?.rows.filter(row => row.include) ?? [];
  }

  *applyBulkImport() {
    const rows = this.includedBulkImportRows.map(({ include, ...row }) => row);
    try {
      const response: ApiResponse = yield this.service.applyBulkImport(rows);
      this.bulkImportResult = response.data.data;
      this.globalStore.notificationStore.showSuccessNotification({ message: `Imported ${pluralize(rows.length, 'row')}.` });
      yield this.refreshAfterChange();
    } catch (error) {
      this.showError(error, 'Error while importing the file.');
    }
  }

  resetBulkImport() {
    this.bulkImportPreview = null;
    this.bulkImportResult = null;
  }

  // ----- helpers ----------------------------------------------------------------------------------------------------

  // Re-read everything the change can affect, keeping the loans grid on its current page.
  private async refreshAfterChange() {
    const refreshes: Promise<unknown>[] = [
      flowResult(this.fetchWarehouseSummary()),
      flowResult(this.loansGridStore.refresh()),
    ];
    if (this.selectedLoanId) {
      refreshes.push(flowResult(this.fetchLoanDetail()), flowResult(this.fetchLoanHistory()));
    }
    await Promise.all(refreshes);
  }

  // GridStore ignores its pageSize constructor argument (meta.pageSize is reset to 200), so set it per request.
  private toPageParams(gridStore: GridStore, pageSize: number): GridPageParams {
    runInAction(() => {
      gridStore.gridData.meta.pageSize = pageSize;
    });
    const { pageNumber, sortBy, sortDir, filters, dropdownFilters } = gridStore.gridData.meta;
    return { page: pageNumber, pageSize, sortBy, sortDir, filters, dropdownFilters };
  }

  private showError(error: any, fallback: string) {
    this.globalStore.notificationStore.showErrorNotification({ message: getApiErrorMessage(error, fallback) });
  }
}
